'use client';

import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';

/**
 * EditorialQuoteSection — `.ps` portfolio-strategy band.
 *
 * Dark night-surface band with a large serif pull-quote (highlighted fragment in
 * teal), an attribution block (name / role / cited source link), and a
 * supporting "old model vs new model" allocation comparison on the right.
 *
 * Source: design-refs/source/assets/private-home.css (`.ps`, `.ps-inner`,
 * `.ps-quote`, `.ps-attr*`, `.ps-bar*`, `.ps-seg*`) + private-home.js
 * `buildPortfolioStrategy()`.
 */

export interface QuoteAttribution {
  name: string;
  role: string;
  /** Cited source label (renders as an underlined link if `sourceHref` set). */
  source?: string;
  sourceHref?: string;
}

export interface AllocationSegment {
  label: string;
  /** Percentage value rendered after the label (e.g. "60%"). */
  value: string;
  /** Flex weight (the source uses the numeric percent as the flex basis). */
  flex: number;
  /** Visual variant: muted "old" tones or the emphasised "private" tone. */
  variant: 'old' | 'old2' | 'newStock' | 'newBond' | 'newPrivate';
}

export interface AllocationModel {
  /** Tag pill, e.g. "Then" / "Now". */
  tag: string;
  /** Title, e.g. "The Old Model". */
  title: string;
  /** Highlight the tag pill (champagne) — used for the "Now" model. */
  highlightTag?: boolean;
  segments: AllocationSegment[];
}

export interface EditorialQuoteSectionProps {
  /** Eyebrow above the quote (e.g. "— The New Portfolio —"). */
  eyebrow?: string;
  /** Pull-quote text. May contain a `highlight` substring rendered in teal. */
  quote?: ReactNode;
  attribution?: QuoteAttribution;
  /** Muted legal note beneath the attribution. */
  disclaimer?: string;
  /** Right-column allocation comparison models. */
  models?: AllocationModel[];
  className?: string;
  'data-cy'?: string;
}

const DEFAULT_EYEBROW = '— The New Portfolio —';

const DEFAULT_ATTRIBUTION: QuoteAttribution = {
  name: 'Larry Fink',
  role: 'CEO of BlackRock',
  source: "2025 Annual Chairman's Letter, March 2025 →",
  sourceHref:
    'https://www.blackrock.com/corporate/investor-relations/2025-larry-fink-annual-chairmans-letter',
};

const DEFAULT_DISCLAIMER =
  'This statement reflects the view of the individual cited as of the date noted and is provided for general context. It is not an endorsement of StartEngine, StartEngine Primary, LLC, or any offering, and is not investment advice or a recommendation.';

const DEFAULT_MODELS: AllocationModel[] = [
  {
    tag: 'Then',
    title: 'The Old Model',
    segments: [
      { label: 'Stocks', value: '60%', flex: 60, variant: 'old' },
      { label: 'Bonds', value: '40%', flex: 40, variant: 'old2' },
    ],
  },
  {
    tag: 'Now',
    title: 'The New Model',
    highlightTag: true,
    segments: [
      { label: 'Stocks', value: '50%', flex: 50, variant: 'newStock' },
      { label: 'Bonds', value: '30%', flex: 30, variant: 'newBond' },
      { label: 'Private', value: '20%', flex: 20, variant: 'newPrivate' },
    ],
  },
];

/* `.ps-hl` */
const HighlightSpan = styled('span')({
  color: COLORS.teal,
});

function Highlight({ children, ...rest }: { children: ReactNode; 'data-cy'?: string }) {
  return <HighlightSpan {...rest}>{children}</HighlightSpan>;
}

const DEFAULT_QUOTE = (
  <>
    &ldquo;The future standard portfolio may look more like{' '}
    <Highlight data-cy="editorial-quote-highlight">50/30/20</Highlight>
    {' — '}
    stocks, bonds, and private assets.&rdquo;
  </>
);

/* `.ps` */
const Band = styled('section')({
  background: COLORS.night,
  borderTop: `1px solid ${COLORS.gold}`,
});

/* `.ps-inner` */
const Inner = styled('div')({
  maxWidth: '1240px',
  margin: '0 auto',
  padding: '80px var(--pad)',
  display: 'grid',
  gridTemplateColumns: '0.92fr 1.08fr',
  gap: '76px',
  alignItems: 'center',
  '@media (max-width: 1080px)': {
    gridTemplateColumns: '1fr',
    gap: '44px',
  },
  '@media (max-width: 768px)': {
    padding: '54px var(--pad) 58px',
  },
});

/* `.ps-eyebrow` */
const Eyebrow = styled('span')({
  display: 'block',
  fontFamily: FONTS.sans,
  fontWeight: 600,
  fontSize: '11px',
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6B879',
  marginBottom: '22px',
});

/* `.ps-quote` */
const Quote = styled('p')({
  margin: 0,
  fontFamily: FONTS.serif,
  fontSize: '34px',
  fontWeight: 400,
  lineHeight: 1.34,
  letterSpacing: '-0.01em',
  color: '#fff',
  textWrap: 'pretty',
  '@media (max-width: 768px)': { fontSize: '27px' },
});

/* `.ps-attr` */
const Attr = styled('div')({
  marginTop: '30px',
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
});

const AttrName = styled('span')({
  fontFamily: FONTS.sans,
  fontSize: '15px',
  color: COLORS.onDark2,
});

const AttrRole = styled('span')({
  fontFamily: FONTS.sans,
  fontSize: '15px',
  fontWeight: 600,
  color: COLORS.teal,
});

const AttrSource = styled('a')({
  marginTop: '8px',
  fontFamily: FONTS.sans,
  fontSize: '12.5px',
  letterSpacing: '0.02em',
  color: 'rgba(233,215,182,0.6)',
  textDecoration: 'underline',
  textDecorationColor: 'rgba(233,215,182,0.3)',
  textUnderlineOffset: '3px',
  alignSelf: 'flex-start',
  maxWidth: '340px',
  transition: 'color 0.15s, text-decoration-color 0.15s',
  '&:hover': {
    color: COLORS.champagne,
    textDecorationColor: COLORS.champagne,
  },
});

/* `.ps-bar-label` */
const BarLabel = styled('div', {
  shouldForwardProp: (prop) => prop !== 'spaced',
})<{ spaced?: boolean }>(({ spaced }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontFamily: FONTS.sans,
  fontSize: '19px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: '#fff',
  marginBottom: '16px',
  ...(spaced ? { marginTop: '38px' } : null),
}));

/* `.ps-bar-tag` (+ `.ps-bar-tag-new`) */
const BarTag = styled('span', {
  shouldForwardProp: (prop) => prop !== 'highlight',
})<{ highlight?: boolean }>(({ highlight }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: FONTS.sans,
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  borderRadius: '999px',
  padding: '5px 10px',
  ...(highlight
    ? { color: '#2A1C03', background: COLORS.champagne }
    : { color: COLORS.onDark3, background: 'rgba(233,215,182,0.1)' }),
}));

/* `.ps-bar` */
const Bar = styled('div')({
  display: 'flex',
  gap: '6px',
  height: '56px',
  minWidth: 0,
});

const segVariants: Record<AllocationSegment['variant'], object> = {
  old: { background: '#1C5A55' },
  old2: { background: '#103A3E' },
  newStock: { background: '#1C5A55' },
  newBond: { background: '#103A3E' },
  newPrivate: {
    background: '#E6B23E',
    color: '#2A1C03',
    boxShadow: '0 0 0 1px rgba(230,178,62,0.5), 0 8px 22px rgba(230,178,62,0.28)',
    '& span': { color: '#2A1C03', opacity: 0.72, fontWeight: 400, marginLeft: '7px' },
  },
};

/* `.ps-seg` (+ variant + `.ps-seg span`) */
const Seg = styled('div', {
  shouldForwardProp: (prop) => prop !== 'segVariant',
})<{ segVariant: AllocationSegment['variant'] }>(({ segVariant }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0 20px',
  borderRadius: '12px',
  fontFamily: FONTS.sans,
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  minWidth: 0,
  '& span': {
    fontWeight: 400,
    opacity: 0.84,
    marginLeft: '7px',
  },
  '@media (max-width: 640px)': {
    padding: '0 12px',
    fontSize: '13px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '2px',
    '& span': {
      marginLeft: 0,
    },
  },
  ...segVariants[segVariant],
}));

const Disclaimer = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  color: 'rgba(255,255,255,0.4)',
  lineHeight: 1.6,
  marginTop: '30px',
  maxWidth: '440px',
  textWrap: 'pretty',
});

export function EditorialQuoteSection({
  eyebrow = DEFAULT_EYEBROW,
  quote = DEFAULT_QUOTE,
  attribution = DEFAULT_ATTRIBUTION,
  disclaimer = DEFAULT_DISCLAIMER,
  models = DEFAULT_MODELS,
  className,
  'data-cy': dataCy = 'editorial-quote-section',
}: EditorialQuoteSectionProps) {
  return (
    <Band className={className} data-cy={dataCy}>
      <Inner className="ps-inner">
        <div className="ps-quote-col">
          {eyebrow && (
            <Eyebrow className="ps-eyebrow" data-cy="editorial-quote-eyebrow">
              {eyebrow}
            </Eyebrow>
          )}
          <Quote className="ps-quote" data-cy="editorial-quote-text">
            {quote}
          </Quote>
          {attribution && (
            <Attr className="ps-attr" data-cy="editorial-quote-attribution">
              <AttrName className="ps-attr-name">{attribution.name}</AttrName>
              <AttrRole className="ps-attr-role">{attribution.role}</AttrRole>
              {attribution.source &&
                (attribution.sourceHref ? (
                  <AttrSource
                    className="ps-attr-source"
                    href={attribution.sourceHref}
                    target="_blank"
                    rel="noopener"
                    data-cy="editorial-quote-source"
                  >
                    {attribution.source}
                  </AttrSource>
                ) : (
                  <AttrSource as="span" className="ps-attr-source" data-cy="editorial-quote-source">
                    {attribution.source}
                  </AttrSource>
                ))}
            </Attr>
          )}
          {disclaimer && <Disclaimer data-cy="editorial-quote-disclaimer">{disclaimer}</Disclaimer>}
        </div>
        <div className="ps-bars" data-cy="editorial-quote-models">
          {models.map((model, modelIndex) => (
            <div key={model.title}>
              <BarLabel className="ps-bar-label" spaced={modelIndex > 0}>
                <BarTag className="ps-bar-tag" highlight={model.highlightTag}>
                  {model.tag}
                </BarTag>
                {model.title}
              </BarLabel>
              <Bar className="ps-bar">
                {model.segments.map((seg) => (
                  <Seg
                    key={`${model.title}-${seg.label}`}
                    className="ps-seg"
                    segVariant={seg.variant}
                    style={{ flex: seg.flex }}
                  >
                    {seg.label} <span>{seg.value}</span>
                  </Seg>
                ))}
              </Bar>
            </div>
          ))}
        </div>
      </Inner>
    </Band>
  );
}

export default EditorialQuoteSection;
